import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ButtonsLocation, BookViewMode, NAVIGATION_ITEMS, NavigationItem } from '../types';
import { EpubJsBook } from '../types/epubjs';

type ReaderState = {
  bookId?: string | null;
  book?: EpubJsBook;
  bookTitle?: string;
  bookAuthors?: string;
  bookCoverUrl?: string;
  bookError?: string;
  fileUrl?: string;
  readingSession?: string | null;
  sourceApplication?: string | null;
  serviceTheme: string;
  isFrogBook: boolean;
  bookType?: 'audio-book' | 'book' | 'scrolled-book';
  viewMode: BookViewMode;
  isNative: boolean;
  navigationButtonLocation: string;
  navigationItems?: NavigationItem[];
  annotationItems?: NavigationItem[];
  annotationButtonLocation: ButtonsLocation;
  hasGoToStart: boolean;
  preModeSwitchLocation?: string | null;
};

const defaultState: ReaderState = {
  bookId: null,
  book: undefined,
  bookTitle: undefined,
  bookAuthors: undefined,
  bookCoverUrl: undefined,
  bookError: undefined,
  fileUrl: undefined,
  readingSession: null,
  sourceApplication: null,
  serviceTheme: 'default',
  isFrogBook: false,
  bookType: undefined,
  viewMode: 'paginated',
  isNative: false,
  navigationItems: [NAVIGATION_ITEMS.TABLE_OF_CONTENT],
  navigationButtonLocation: 'toolbar',
  annotationItems: [],
  annotationButtonLocation: 'toolbar',
  hasGoToStart: false,
  preModeSwitchLocation: null,
};

const useReaderStore = defineStore('book-player-reader', () => {
  const readerState = ref<ReaderState>({ ...defaultState });

  const bookId = computed(() => readerState.value.bookId);
  const book = computed(() => readerState.value.book);
  const bookTitle = computed(() => readerState.value.bookTitle);
  const bookAuthors = computed(() => readerState.value.bookAuthors);
  const bookCoverUrl = computed(() => readerState.value.bookCoverUrl);
  const readingSession = computed(() => readerState.value.readingSession);
  const sourceApplication = computed(() => readerState.value.sourceApplication);
  const fileUrl = computed(() => readerState.value.fileUrl);
  const bookError = computed(() => readerState.value.bookError);
  const serviceTheme = computed(() => readerState.value.serviceTheme);
  const isFrogBook = computed(() => readerState.value.isFrogBook);
  const isNative = computed(() => readerState.value.isNative);
  const viewMode = computed(() => readerState.value.viewMode);
  const isContinuous = computed(() => viewMode.value === 'continuous');
  const isPaginated = computed(() => viewMode.value === 'paginated');
  const isAudioOnly = computed(() => viewMode.value === 'audio-only');
  const isLocalEpubFile = computed(
    () => !fileUrl.value || fileUrl.value?.startsWith('http://localhost') || fileUrl.value?.includes('capacitor'),
  );
  const isTocTop = computed(() => readerState.value.navigationButtonLocation === 'toolbar');
  const isTocBottom = computed(() => readerState.value.navigationButtonLocation === 'controls');
  const navigationItems = computed(() => readerState.value.navigationItems);

  // New unified annotation properties
  const annotationItems = computed(() => readerState.value.annotationItems || []);
  const isAnnotationBtnsToolbar = computed(() => readerState.value.annotationButtonLocation === 'toolbar');
  const isAnnotationBtnsControls = computed(() => readerState.value.annotationButtonLocation === 'controls');

  // Computed from annotationItems (for backward compatibility)
  const hasHighlights = computed(() =>
    [...annotationItems.value, ...(navigationItems.value ?? [])].includes(NAVIGATION_ITEMS.HIGHLIGHT_LIST),
  );
  const hasBookmarks = computed(() =>
    [...annotationItems.value, ...(navigationItems.value ?? [])].includes(NAVIGATION_ITEMS.BOOKMARKS_LIST),
  );
  const isBookmarkBtnsToolbar = computed(() => readerState.value.annotationButtonLocation === 'toolbar');
  const isBookmarkBtnsControls = computed(() => readerState.value.annotationButtonLocation === 'controls');
  const isHighlightBtnsToolbar = computed(() => readerState.value.annotationButtonLocation === 'toolbar');
  const isHighlightBtnsControls = computed(() => readerState.value.annotationButtonLocation === 'controls');
  const isScrolledBook = computed(() => readerState.value?.bookType === 'scrolled-book');
  const hasGoToStart = computed(() => readerState.value.hasGoToStart);

  const setServiceTheme = (themeName: string) => {
    readerState.value.serviceTheme = themeName;
  };

  const setBookId = (_bookId?: string) => {
    readerState.value.bookId = _bookId;
  };
  const setBook = (_book?: EpubJsBook) => {
    readerState.value.book = _book;
  };
  const setBookTitle = (_bookTitle?: string) => {
    readerState.value.bookTitle = _bookTitle;
  };
  const setBookAuthors = (_bookAuthors?: string) => {
    readerState.value.bookAuthors = _bookAuthors;
  };
  const setBookCoverUrl = (_bookCoverUrl?: string) => {
    readerState.value.bookCoverUrl = _bookCoverUrl;
  };
  const setBookError = (_bookError: string) => {
    readerState.value.bookError = _bookError;
  };
  const setFileUrl = (_fileUrl: string) => {
    readerState.value.fileUrl = _fileUrl;
  };
  const setReadingSession = (session: string) => {
    readerState.value.readingSession = session;
  };
  const setSourceApplication = (source: string) => {
    readerState.value.sourceApplication = source;
  };

  const setFrogBook = (_isFrogBook: boolean) => {
    readerState.value.isFrogBook = _isFrogBook;
  };

  const setBookType = (_bookType?: 'audio-book' | 'book' | 'scrolled-book') => {
    readerState.value.bookType = _bookType;
  };

  const setIsNative = (_isNative: boolean) => {
    readerState.value.isNative = _isNative;
  };

  const setViewMode = (_viewMode: BookViewMode) => {
    readerState.value.viewMode = _viewMode;
  };

  const setNavigationItems = (
    _navigationItems: NavigationItem[] | undefined,
    location: ButtonsLocation = 'toolbar',
  ) => {
    readerState.value.navigationItems = _navigationItems;
    readerState.value.navigationButtonLocation = location;
  };

  const setAnnotationItems = (
    _annotationItems: NavigationItem[] | undefined,
    location: ButtonsLocation = 'toolbar',
  ) => {
    readerState.value.annotationItems = _annotationItems;
    readerState.value.annotationButtonLocation = location;
  };

  const setGoToStart = (_hasGoToStart: boolean) => {
    readerState.value.hasGoToStart = _hasGoToStart;
  };

  const $reset = () => {
    readerState.value = { ...defaultState };
  };

  const resetState = () => {
    $reset();
  };

  return {
    bookId,
    book,
    bookTitle,
    bookAuthors,
    bookCoverUrl,
    readingSession,
    sourceApplication,
    fileUrl,
    bookError,
    serviceTheme,
    isFrogBook,
    isScrolledBook,
    isNative,
    viewMode,
    isLocalEpubFile,
    isTocTop,
    isTocBottom,
    isContinuous,
    isPaginated,
    isAudioOnly,
    navigationItems,
    annotationItems,
    isAnnotationBtnsToolbar,
    isAnnotationBtnsControls,
    hasHighlights,
    hasBookmarks,
    isBookmarkBtnsToolbar,
    isBookmarkBtnsControls,
    isHighlightBtnsToolbar,
    isHighlightBtnsControls,
    hasGoToStart,
    setBookId,
    setBook,
    setBookTitle,
    setBookAuthors,
    setBookCoverUrl,
    setBookError,
    setFileUrl,
    setReadingSession,
    setSourceApplication,
    setServiceTheme,
    setFrogBook,
    setBookType,
    setIsNative,
    setViewMode,
    resetState,
    setNavigationItems,
    setAnnotationItems,
    setGoToStart,
  };
});

export default useReaderStore;
